/**
 * PRD Breakdown Status Panel Component
 *
 * Displays real-time status, progress, timer, and cancellation controls
 * for active PRD breakdown jobs.
 */

import type { JSX } from 'react';
import { useState } from 'react';
import type { ChipProps } from '@heroui/react';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  BanIcon,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  X,
  XCircle,
} from 'lucide-react';

import { useElapsedTime } from '~/hooks/useElapsedTime';
import type { ProjectBreakdownStatus } from '~/services/api/prd';
import type { CancelBreakdownResponse } from '~/services/api/prd';
import { useSSEStore } from '~/stores/sseStore';
import { formatRelativeTime } from '~/utils/formatDuration';
import { logger } from '~/utils/logger';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

interface PRDBreakdownStatusPanelProps {
  projectId: string;
  status: ProjectBreakdownStatus;
  onCancel: () => Promise<CancelBreakdownResponse | void>;
  isCanceling: boolean;
  onDismiss: () => void;
}

/**
 * Status badge configuration for different breakdown states.
 */
interface StatusConfig {
  color: ChipProps['color'];
  icon: LucideIcon;
  label: string;
  pulse?: boolean;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  idle: {
    color: 'default',
    icon: Clock,
    label: 'Idle',
  },
  PENDING: {
    color: 'warning',
    icon: Clock,
    label: 'Pending',
  },
  PROCESSING: {
    color: 'primary',
    icon: Activity,
    label: 'Processing',
    pulse: false, // Spinner is on phase text instead
  },
  COMPLETED: {
    color: 'success',
    icon: CheckCircle2,
    label: 'Completed',
  },
  FAILED: {
    color: 'danger',
    icon: XCircle,
    label: 'Failed',
  },
  CANCELLED: {
    color: 'default',
    icon: BanIcon,
    label: 'Cancelled',
  },
};

/**
 * Format phase name for display.
 */
function formatPhaseName(phase: string | null): string {
  if (!phase) return 'Initializing...';

  // Map agent phases to user-friendly activity descriptions
  const phaseNames: Record<string, string> = {
    // PRD Task Builder phases
    prd_analysis: 'Analyzing PRD structure',
    task_generation: 'Creating tasks',
    backend_enrichment: 'Creating BE tasks',
    frontend_enrichment: 'Creating FE tasks',
    infrastructure_enrichment: 'Creating INF tasks',
    database_enrichment: 'Creating DB tasks',
    testing_enrichment: 'Creating TEST tasks',
    documentation_enrichment: 'Creating DOC tasks',
    validation: 'Validating',
    task_creation: 'Saving',
    completed: 'Complete',

    // PRD Generation phases (if breakdown is rerun)
    classifying: 'Classifying content',
    extracting: 'Extracting requirements',
    integrating: 'Integrating feedback',
    assembling: 'Assembling document',
  };

  return phaseNames[phase] ?? phase.replace(/_/g, ' ');
}

/**
 * Get progress percentage based on current phase.
 * Used as fallback when SSE progress is not available.
 */
function getPhaseProgress(phase: string | null): number {
  if (!phase) return 0;

  const phaseMap: Record<string, number> = {
    prd_analysis: 25,
    task_generation: 50,
    validation: 85,
    completed: 100,
  };

  return phaseMap[phase] ?? 50; // Default to 50% if unknown phase
}

export function PRDBreakdownStatusPanel(props: PRDBreakdownStatusPanelProps): JSX.Element {
  const { isCanceling, onCancel, onDismiss, projectId, status } = props;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Track elapsed time from server timestamps (survives refresh)
  // Call hooks unconditionally - use null-safe timestamps
  const elapsedSecondsFromStart = useElapsedTime(status.breakdown_started_at);
  const elapsedSecondsFromCreation = useElapsedTime(status.breakdown_created_at);

  // Get real-time progress from SSE store (if available)
  const activeJobs = useSSEStore(state => state.activeJobs);
  const activeJob = status.job_id ? activeJobs[status.job_id] : null;

  // Get status configuration
  const statusConfig = STATUS_CONFIG[status.breakdown_status] || STATUS_CONFIG['idle'];
  const StatusIcon = statusConfig.icon;

  // Calculate progress (prefer SSE real-time, fallback to phase-based)
  const progress = activeJob?.progress ?? getPhaseProgress(status.current_phase);

  // Check if cancellable (PENDING or PROCESSING only)
  const isCancellable =
    status.breakdown_status === 'PENDING' || status.breakdown_status === 'PROCESSING';

  // Show panel only when breakdown exists or recently completed
  // Allow user to dismiss completed/failed/cancelled jobs
  const shouldHide =
    !status.has_active_breakdown &&
    status.breakdown_status !== 'COMPLETED' &&
    status.breakdown_status !== 'FAILED' &&
    status.breakdown_status !== 'CANCELLED';

  if (shouldHide) {
    return <></>;
  }

  const handleCancelClick = (): void => {
    logger.info(
      { component: 'PRDBreakdownStatusPanel', projectId, jobId: status.job_id },
      'User initiated breakdown cancellation',
    );
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async (): Promise<void> => {
    try {
      logger.info(
        { component: 'PRDBreakdownStatusPanel', projectId, jobId: status.job_id },
        'Confirming breakdown cancellation',
      );
      await onCancel();
      setShowCancelConfirm(false);
    } catch (error) {
      logger.error(
        { component: 'PRDBreakdownStatusPanel', projectId },
        'Failed to cancel breakdown',
        error instanceof Error ? error : new Error(String(error)),
      );
      // Error handling will be done in parent via react-query
    }
  };

  const handleCancelDialogClose = (): void => {
    setShowCancelConfirm(false);
  };

  return (
    <>
      <Card className="mt-3 bg-surface-elevated border-1 border-divider">
        <CardBody className="gap-3 p-4">
          {/* Header: Status Badge + Phase + Dismiss */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Chip
                color={statusConfig.color}
                size="sm"
                startContent={
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${statusConfig.pulse ? 'animate-pulse' : ''}`}
                  />
                }
                variant="flat"
              >
                {statusConfig.label}
              </Chip>
              {status.breakdown_status === 'PROCESSING' && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 dark:bg-primary/20">
                  <Loader2 className="w-3.5 h-3.5 text-primary dark:text-primary animate-spin" />
                  <span className="text-sm font-medium text-primary dark:text-primary">
                    {status.current_phase
                      ? `${formatPhaseName(status.current_phase)}...`
                      : 'Starting...'}
                  </span>
                </div>
              )}
              {status.breakdown_status === 'COMPLETED' && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-success/10 dark:bg-success/20">
                  <CheckCircle2 className="w-3.5 h-3.5 text-success dark:text-success" />
                  <span className="text-sm font-medium text-success dark:text-success">
                    Complete
                  </span>
                </div>
              )}
            </div>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={onDismiss}
              aria-label="Dismiss status panel"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Timer + Cancel Button */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">
              {status.breakdown_completed_at && status.breakdown_started_at ? (
                <>
                  Duration:{' '}
                  {formatRelativeTime(
                    Math.floor(
                      (new Date(status.breakdown_completed_at).getTime() -
                        new Date(status.breakdown_started_at).getTime()) /
                        1000,
                    ),
                  )}
                </>
              ) : status.breakdown_started_at ? (
                <>Started: {formatRelativeTime(elapsedSecondsFromStart)}</>
              ) : status.breakdown_created_at ? (
                <>Created: {formatRelativeTime(elapsedSecondsFromCreation)}</>
              ) : (
                <>Initializing...</>
              )}
            </span>
            {isCancellable && (
              <Button
                color="danger"
                size="sm"
                variant="light"
                isLoading={isCanceling}
                isDisabled={isCanceling}
                onPress={handleCancelClick}
              >
                Cancel
              </Button>
            )}
          </div>

          {/* Progress Bar */}
          {(status.breakdown_status === 'PENDING' || status.breakdown_status === 'PROCESSING') && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="bg-surface-elevated rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary rounded-full h-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Document Status & Task Count */}
          <div className="flex items-center gap-4 text-sm">
            {/* Tech Spec Indicator */}
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-muted" />
              <span className="text-muted">
                Tech Spec:{' '}
                <span
                  className={`font-medium ${
                    status.has_technical_spec ? 'text-success' : 'text-muted'
                  }`}
                >
                  {status.has_technical_spec ? 'Yes' : 'No'}
                </span>
              </span>
            </div>

            {/* Task Count */}
            {status.task_count > 0 && (
              <div className="text-muted">
                Tasks: <span className="text-foreground font-medium">{status.task_count}</span>
              </div>
            )}
          </div>

          {/* Error Message (for FAILED status) */}
          {status.breakdown_status === 'FAILED' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border-1 border-danger/20">
              <AlertCircle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-danger">
                <div className="font-semibold mb-1">Breakdown Failed</div>
                <div>
                  The task generation process encountered an error. Please try again or contact
                  support if the issue persists.
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Message */}
          {status.breakdown_status === 'CANCELLED' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border-1 border-warning/20">
              <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-warning">
                <div className="font-semibold mb-1">Breakdown Cancelled</div>
                <div>
                  The task generation was cancelled. Note: Some tasks may still have been created.
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Cancel Confirmation Modal with Soft Cancel Warning */}
      {showCancelConfirm && (
        <ConfirmationDialog
          title="Cancel Task Generation?"
          content={
            <div className="space-y-3">
              <p>Are you sure you want to cancel?</p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border-1 border-warning/20">
                <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div className="text-sm text-warning">
                  <div className="font-semibold mb-1">⚠️ Soft Cancellation</div>
                  <p className="mb-2">The breakdown process may continue running briefly.</p>
                  <p className="mb-2">Tasks may still be created after cancellation.</p>
                  <p>Full cancellation support coming soon.</p>
                </div>
              </div>
            </div>
          }
          isOpen={showCancelConfirm}
          isPending={isCanceling}
          onConfirm={() => void handleCancelConfirm()}
          onClose={handleCancelDialogClose}
          confirmText="Cancel Anyway"
          cancelText="Keep Running"
          confirmColor="danger"
        />
      )}
    </>
  );
}
